import type { AnimationDirection } from '../ProductCarouselVideoView';
import type { CarouselImageProperty } from '../ProductImage';
import type { Dispatch, SetStateAction, RefObject } from 'react';

import { gsap } from 'gsap';
import { useEffect, useCallback } from 'react';

import { MOBILE_VIDEO_POSITION } from '~/utilities/constants';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import type { Maybe, Video } from '~/utilities/graphql/codegen';

interface UseCarouselAnimationProps {
  galleryRef: RefObject<HTMLUListElement | null>;
  itemRefs: RefObject<(HTMLLIElement | null)[]>;
  gsapQuery: gsap.utils.SelectorFunc;
  currentImageIndex: number;
  setCurrentImageIndex: Dispatch<SetStateAction<number>>;
  productImageList: CarouselImageProperty[];
  isSwipeMode: boolean;
  setScrollInProgress: Dispatch<SetStateAction<number[]>>;
  isMobile: boolean;
  video?: Maybe<Video>;
}

export const useCarouselAnimation = ({
  galleryRef,
  itemRefs,
  gsapQuery,
  currentImageIndex,
  setCurrentImageIndex,
  productImageList,
  isSwipeMode,
  setScrollInProgress,
  isMobile,
  video,
}: UseCarouselAnimationProps) => {
  const { galleryZoomIn } = useGalleryContext();

  useEffect(() => {
    if (!(video && isMobile)) {
      return;
    }

    setCurrentImageIndex(prevIndex => {
      if (prevIndex >= MOBILE_VIDEO_POSITION) {
        return galleryZoomIn ? prevIndex - 1 : prevIndex + 1;
      }

      return prevIndex;
    });
  }, [galleryZoomIn, video, isMobile, setCurrentImageIndex]);

  const performScrollAnimation = useCallback(
    (nextIndex: number) => {
      gsap.to(galleryRef.current, {
        duration: 0.6,
        ease: 'power2',
        scrollTo: {
          x: itemRefs.current?.[nextIndex] || undefined,
        },
        onStart: () => {
          setScrollInProgress(indexes => [...indexes, nextIndex]);
        },
        onComplete: () => {
          setScrollInProgress(indexes => indexes.filter(i => i !== nextIndex));
        },
      });
    },
    [galleryRef, itemRefs, setScrollInProgress]
  );

  const slideToNextImage = useCallback(
    (newIndex: number) => {
      if (currentImageIndex === newIndex) {
        return;
      }

      let direction: AnimationDirection = 'left';

      if (newIndex < currentImageIndex) {
        if (
          currentImageIndex - newIndex <=
          newIndex + productImageList.length - currentImageIndex
        ) {
          direction = 'right';
        }
      } else if (
        currentImageIndex + productImageList.length - newIndex <
        newIndex - currentImageIndex
      ) {
        direction = 'right';
      }

      if (!isSwipeMode || galleryZoomIn) {
        gsap.to(gsapQuery('.gallery-item')[newIndex], {
          x: direction === 'left' ? '100%' : '-100%',
          duration: 0,
        });
        gsap.to(gsapQuery('.gallery-item')[currentImageIndex], {
          x: direction === 'left' ? '-100%' : '100%',
          duration: 0.6,
        });
        gsap.to(gsapQuery('.gallery-item')[newIndex], {
          x: '0',
          duration: 0.6,
        });
      }

      if (isSwipeMode) {
        performScrollAnimation(newIndex);
      }

      setCurrentImageIndex(newIndex);
    },
    [
      currentImageIndex,
      galleryZoomIn,
      productImageList,
      isSwipeMode,
      setCurrentImageIndex,
      gsapQuery,
      performScrollAnimation,
    ]
  );

  return {
    performScrollAnimation,
    slideToNextImage,
  };
};
